import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { documentsAPI, clientsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Search, Upload, FileText, Download, Loader2, Trash2, Eye, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';

const Documents = () => {
  const { hasPermission } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [uploadDialog, setUploadDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: '',
    clientId: '',
    type: 'Tax Return',
    file: null
  });
  const { toast } = useToast();
  
  // Check permissions
  const canDeleteDocument = hasPermission('canDeleteDocument');

  useEffect(() => {
    fetchDocuments();
    fetchClients();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsAPI.getAll();
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({});
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewDocument(prev => ({
        ...prev,
        file,
        name: prev.name || file.name
      }));
    }
  };

  const handleUploadDocument = async () => {
    if (!newDocument.name || !newDocument.clientId) {
      toast({ title: 'Error', description: 'Name and client are required', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const client = clients.find(c => c._id === newDocument.clientId);
      let fileUrl = null;
      let fileSize = 'N/A';

      // Upload the file if selected
      if (newDocument.file) {
        const formData = new FormData();
        formData.append('file', newDocument.file);
        formData.append('clientId', newDocument.clientId);

        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/upload`, {
          method: 'POST',
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          fileUrl = uploadData.fileUrl;
          fileSize = `${(newDocument.file.size / 1024).toFixed(1)} KB`;
        } else {
          throw new Error('File upload failed');
        }
      }

      // Create document record
      const docData = {
        name: newDocument.name,
        clientId: newDocument.clientId,
        clientName: client?.name || '',
        type: newDocument.type,
        status: 'pending_review',
        size: fileSize,
        uploadedBy: 'John Anderson',
        fileUrl: fileUrl
      };

      await documentsAPI.create(docData);
      toast({ title: 'Success', description: 'Document uploaded successfully' });
      setUploadDialog(false);
      setNewDocument({ name: '', clientId: '', type: 'Tax Return', file: null });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentsAPI.delete(docId);
      toast({ title: 'Success', description: 'Document deleted' });
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    }
  };

  const handleViewDocument = (doc) => {
    setViewingDoc(doc);
    setViewDialog(true);
  };

  const handleDownloadDocument = (doc) => {
    if (doc.fileUrl) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.fileUrl}`, '_blank');
    } else {
      toast({ title: 'Info', description: 'No file attached to this document' });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doc.clientName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || doc.type === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle date sorting
      if (sortField === 'createdAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      // Handle string sorting
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending_review':
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_signature':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const documentTypes = ['Tax Return', 'Financial Statement', 'Receipt', 'Contract', 'Other'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">Manage all client documents and files</p>
        </div>
        <Button onClick={() => setUploadDialog(true)} className="gap-2" data-testid="upload-document-btn">
          <Upload className="h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={typeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('all')}
              >
                All Types
              </Button>
              {documentTypes.map((type) => (
                <Button
                  key={type}
                  variant={typeFilter === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTypeFilter(type)}
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-auto font-medium hover:bg-transparent"
                    onClick={() => handleSort('name')}
                  >
                    Document {getSortIcon('name')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-auto font-medium hover:bg-transparent"
                    onClick={() => handleSort('clientName')}
                  >
                    Client {getSortIcon('clientName')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-auto font-medium hover:bg-transparent"
                    onClick={() => handleSort('type')}
                  >
                    Type {getSortIcon('type')}
                  </Button>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-auto font-medium hover:bg-transparent"
                    onClick={() => handleSort('createdAt')}
                  >
                    Date {getSortIcon('createdAt')}
                  </Button>
                </TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedDocuments.map((doc) => (
                <TableRow key={doc._id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">{doc.size || 'N/A'}</p>
                      </div>
                      {doc.fileUrl && (
                        <Badge variant="outline" className="text-xs text-green-600">File</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-700">{doc.clientName}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${getStatusColor(doc.status)}`}>
                      {(doc.status || 'pending').replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {doc.createdAt ? format(new Date(doc.createdAt), 'MMM dd, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell className="text-gray-700">{doc.uploadedBy || 'N/A'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => handleViewDocument(doc)}
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => handleDownloadDocument(doc)}
                        disabled={!doc.fileUrl}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      {canDeleteDocument && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(doc._id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedDocuments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No documents found</h3>
                    <p className="text-gray-500">Upload a document to get started</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Name *</Label>
              <Input
                placeholder="e.g., 2024 Tax Return"
                value={newDocument.name}
                onChange={(e) => setNewDocument(prev => ({ ...prev, name: e.target.value }))}
                data-testid="document-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={newDocument.clientId} onValueChange={(v) => setNewDocument(prev => ({ ...prev, clientId: v }))}>
                <SelectTrigger data-testid="document-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client._id} value={client._id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={newDocument.type} onValueChange={(v) => setNewDocument(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Choose File</Label>
              <Input
                type="file"
                onChange={handleFileChange}
                className="cursor-pointer"
                data-testid="document-file-input"
              />
              {newDocument.file && (
                <p className="text-sm text-gray-500">
                  Selected: {newDocument.file.name} ({(newDocument.file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploading} data-testid="upload-document-submit">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
          </DialogHeader>
          {viewingDoc && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{viewingDoc.name}</h3>
                  <p className="text-sm text-gray-500">{viewingDoc.type}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 py-4 border-y">
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{viewingDoc.clientName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusColor(viewingDoc.status)}>
                    {(viewingDoc.status || 'pending').replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Size</p>
                  <p className="font-medium">{viewingDoc.size || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Uploaded By</p>
                  <p className="font-medium">{viewingDoc.uploadedBy || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Upload Date</p>
                  <p className="font-medium">
                    {viewingDoc.createdAt ? format(new Date(viewingDoc.createdAt), 'MMM dd, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">File</p>
                  <p className="font-medium">
                    {viewingDoc.fileUrl ? (
                      <Badge variant="outline" className="text-green-600">Attached</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">No file</Badge>
                    )}
                  </p>
                </div>
              </div>

              {viewingDoc.fileUrl && (
                <div className="flex gap-2">
                  <Button 
                    className="flex-1 gap-2"
                    onClick={() => handleDownloadDocument(viewingDoc)}
                  >
                    <Download className="h-4 w-4" />
                    Download File
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${viewingDoc.fileUrl}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in New Tab
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
